import { differenceBy } from 'mojito/utils';

const EMPTY_CARDS = [];

/**
 * Promotion service utilities.
 *
 * @class PromotionsUtils
 * @name utils
 * @memberof Mojito.Services.Promotions
 */
export default class PromotionsUtils {
    /**
     * Creates a promotion container channel key.
     *
     * @param {string} containerId - Promotion container id.
     * @param {string} channel - Promotion channel.
     * @returns {string} Promotion container channel key.
     *
     * @function Mojito.Services.Promotions.utils.createContainerChannelKey
     */
    static createContainerChannelKey(containerId, channel) {
        return `${containerId}|${channel}`;
    }

    /**
     * Parses a promotion container channel key.
     *
     * @param {string} channelKey - Promotion container channel key.
     * @returns {{containerId: string, channel: string}} Promotion container channel key parts.
     *
     * @function Mojito.Services.Promotions.utils.parseContainerChannelKey
     */
    static parseContainerChannelKey(channelKey) {
        const [containerId, channel] = channelKey.split('|');
        return { containerId, channel };
    }

    /**
     * Merge promotion location objects. If there are promotions with the same id, the ones from the second map will be used.
     * In a map key is location id, and value is a list of promotions for that location.
     *
     * @param {Map<string, Array<Mojito.Services.Promotions.types.Promotion>>} [l1] - First promotion locations map.
     * @param {Map<string, Array<Mojito.Services.Promotions.types.Promotion>>} [l2] - Second promotion locations map.
     *
     * @returns {Map<string, Array<Mojito.Services.Promotions.types.Promotion>>} Merged promotion locations.
     *
     * @function Mojito.Services.Promotions.utils.mergeLocations
     */
    static mergeLocations(l1 = {}, l2 = {}) {
        const mergedPromotions = Object.keys(l2).reduce((acc, locationId) => {
            acc[locationId] = PromotionsUtils.mergePromotions(l1[locationId], l2[locationId]);
            return acc;
        }, {});
        return { ...l1, ...mergedPromotions };
    }

    /**
     * Merge promotion lists. If there are promotions with the same id, the ones from the second list will be used.
     * The merged list will be sorted by displayOrder.
     *
     * @param {Array<Mojito.Services.Promotions.types.Promotion>} [p1] - First promotions list.
     * @param {Array<Mojito.Services.Promotions.types.Promotion>} [p2] - Second promotions list.
     *
     * @returns {Array<Mojito.Services.Promotions.types.Promotion>} List of merged promotions.
     *
     * @function Mojito.Services.Promotions.utils.mergePromotions
     */
    static mergePromotions(p1 = [], p2 = []) {
        const locations = differenceBy(p1, p2, 'id');
        return [...locations, ...p2].sort((a, b) => a.displayOrder - b.displayOrder);
    }

    /**
     * Picks the cards of a promotion container that apply to the given page and sorts them by display order.
     *
     * @param {Mojito.Services.Promotions.types.PromotionContainer} [container] - Promotion container.
     * @param {string} [page] - Applicable page name. All cards are kept if omitted.
     * @returns {Array<object>} Promotion container cards.
     *
     * @function Mojito.Services.Promotions.utils.resolveCardsFromContainer
     */
    static resolveCardsFromContainer(container, page) {
        const cards = container?.cards;
        if (!cards) {
            return EMPTY_CARDS;
        }
        return cards
            .filter(
                card =>
                    !page || !card.applicablePages?.length || card.applicablePages.includes(page)
            )
            .sort((a, b) => {
                const nameA = a.name || '';
                const nameB = b.name || '';
                return nameA.localeCompare(nameB);
            })
            .sort((a, b) => a.displayOrder - b.displayOrder);
    }
}
